import React from "react";
import {
  CONTRACT_VARIABLE_KEYS,
  CONTRACT_VARIABLE_SCHEMA,
  canonicalizeContractVariableKey,
} from "../../../services/gestionDocumentos/contractVariableSchema";

export const TOKENS_FRECUENTES = CONTRACT_VARIABLE_KEYS;
export const VARIABLE_LABEL_MAP = CONTRACT_VARIABLE_SCHEMA.reduce((acc, item) => {
  acc[item.key] = item.label;
  return acc;
}, {});

export const PLANTILLA_INICIAL = [
  {
    id: "clausula-1",
    title: "Primera: Partes contratantes",
    content:
      "Entre {{empresa}}, con RUC {{ruc_empresa}}, representada por {{representante_legal}}, y el trabajador {{trabajador_nombre}} identificado con DNI {{trabajador_dni}}.",
  },
  {
    id: "clausula-2",
    title: "Segunda: Objeto del contrato",
    content:
      "El trabajador prestará servicios como {{cargo}} en {{lugar_trabajo}}, bajo la modalidad de {{tipo_contrato}}.",
  },
  {
    id: "clausula-3",
    title: "Tercera: Remuneración",
    content: "La remuneración mensual será de S/ {{sueldo}} (soles), pagada conforme a ley.",
  },
];

export const DATOS_INICIALES = {
  empresa: "",
  ruc_empresa: "",
  empleador_domicilio_legal: "",
  representante_legal: "",
  representante_legal_cargo: "",
  trabajador_nombre: "",
  trabajador_dni: "",
  trabajador_domicilio: "",
  trabajador_telefono: "",
  trabajador_correo: "",
  cargo: "",
  codigo_ocupacional: "",
  sueldo: "",
  monto_sueldo: "",
  moneda_sueldo: "Soles",
  fecha_inicio: "",
  fecha_fin: "",
  periodo_prueba_meses: "3",
  fecha_fin_periodo_prueba: "",
  tipo_contrato: "",
  jornada: "",
  lugar_trabajo: "",
  ciudad_jurisdiccion: "",
  ciudad_firma: "",
  fecha_firma: "",
  firma_empleador_nombre: "",
  firma_empleador_cargo: "",
  firma_trabajador_nombre: "",
  firma_trabajador_dni: "",
};

export const STEP_KEYS = ["paso-1", "paso-2", "paso-3", "paso-4", "paso-5"];
export const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["clean"],
  ],
};
export const QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "bullet", "align"];
export const PDF_MARGIN_PRESETS = {
  ESTRECHO: { pageMarginPt: 42, innerPaddingPx: 0, label: "Estrecho" },
  NORMAL: { pageMarginPt: 56, innerPaddingPx: 0, label: "Normal" },
  AMPLIO: { pageMarginPt: 70, innerPaddingPx: 0, label: "Amplio" },
};
export const A4_WIDTH_PT = 595.28;
export const PX_TO_PT = 72 / 96;
export const PT_TO_MM = 25.4 / 72;

export const toTemplateKey = (ruc) => `contrato_template_${ruc || "default"}`;
export const variableLabelFor = (key, fallback = "") => VARIABLE_LABEL_MAP[key] || fallback || key;
export const toSafeText = (value) => String(value ?? "").trim();
export const toDateText = (value) => {
  if (value === null || typeof value === "undefined") return "";
  if (typeof value === "string") return value.trim();
  if (value?.seconds) {
    return new Date(value.seconds * 1000).toLocaleDateString("es-PE");
  }
  if (value instanceof Date) return value.toLocaleDateString("es-PE");
  return String(value).trim();
};
export const getFullWorkerName = (worker = {}) =>
  [worker?.nombres, worker?.apellidos]
    .map((value) => toSafeText(value))
    .filter(Boolean)
    .join(" ")
    .trim();
export const getUserRuc = (usuario = {}) =>
  toSafeText(usuario?.ruc || usuario?.ruc_empresa || usuario?.RUC || usuario?.rucEmpresa);
export const SPANISH_MONTH_MAP = {
  enero: "01",
  febrero: "02",
  marzo: "03",
  abril: "04",
  mayo: "05",
  junio: "06",
  julio: "07",
  agosto: "08",
  setiembre: "09",
  septiembre: "09",
  octubre: "10",
  noviembre: "11",
  diciembre: "12",
};
export const toIsoDateString = (value) => {
  if (!value) return "";

  if (value?.seconds) {
    const dt = new Date(value.seconds * 1000);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const source = toSafeText(value);
  if (!source) return "";

  const isoMatch = source.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  const latinMatch = source.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (latinMatch) {
    const [, d, m, y] = latinMatch;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  const lower = source.toLowerCase();
  const humanMonthMatch = lower.match(/(\d{1,2})\s*(?:de)?\s*([a-záéíóúñ]+)\s*(?:de)?\s*(\d{4})/i);
  if (humanMonthMatch) {
    const day = String(humanMonthMatch[1]).padStart(2, "0");
    const monthName = humanMonthMatch[2]
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const month = SPANISH_MONTH_MAP[monthName];
    const year = humanMonthMatch[3];
    if (month) return `${year}-${month}-${day}`;
  }

  const verboseMonthMatch = lower.match(/(\d{1,2})\s*d[ií]as?\s+del\s+mes\s+de\s+([a-záéíóúñ]+)\s+de\s+(\d{4})/i);
  if (verboseMonthMatch) {
    const day = String(verboseMonthMatch[1]).padStart(2, "0");
    const monthName = verboseMonthMatch[2]
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const month = SPANISH_MONTH_MAP[monthName];
    const year = verboseMonthMatch[3];
    if (month) return `${year}-${month}-${day}`;
  }

  return "";
};

export const formatSoles = (amount) => {
  if (amount === null || typeof amount === "undefined" || amount === "") return "";
  const str = String(amount).trim();
  if (!str) return "";
  if (/^s\/\.?\s*/i.test(str)) {
    return str.replace(/^s\/\.?\s*/i, "S/ ");
  }
  const cleanStr = str.replace(/[^0-9.,-]/g, "").replace(/,/g, "");
  const num = parseFloat(cleanStr);
  if (Number.isNaN(num)) return str;
  return `S/ ${num.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatMonto = (amount) => {
  if (amount === null || typeof amount === "undefined" || amount === "") return "";
  const str = String(amount).trim();
  if (!str) return "";
  const cleanStr = str.replace(/^s\/\.?\s*/i, "").replace(/[^0-9.,-]/g, "").replace(/,/g, "");
  const num = parseFloat(cleanStr);
  if (Number.isNaN(num)) return str;
  return num.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const calcularFinPeriodoPrueba = (fechaInicio, meses = 3) => {
  const iso = toIsoDateString(fechaInicio);
  if (!iso) return "";
  const parts = iso.split("-").map(Number);
  if (parts.length !== 3) return "";
  const [y, m, d] = parts;
  if (!y || !m || !d) return "";

  const targetMonthIndex = m - 1 + Number(meses || 3);
  const targetYear = y + Math.floor(targetMonthIndex / 12);
  const normalizedMonth = ((targetMonthIndex % 12) + 12) % 12;
  const maxDays = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  const clampedDay = Math.min(d, maxDays);

  const resY = targetYear;
  const resM = String(normalizedMonth + 1).padStart(2, "0");
  const resD = String(clampedDay).padStart(2, "0");
  return `${resY}-${resM}-${resD}`;
};

export const formatDateEsPe = (value) => {
  const iso = toIsoDateString(value);
  if (!iso) return toSafeText(value);
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export const isHtmlContent = (text) => /<\/?[a-z][\s\S]*>/i.test(String(text || ""));

export const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const normalizeQuillHtml = (html) => {
  let value = String(html || "").trim();
  if (!value || value === "<p><br></p>") return "";

  // Limpia párrafos vacíos repetidos que suelen aparecer al editar con Quill.
  value = value
    .replace(/<p>\s*(?:<br\s*\/?>|&nbsp;|\s)*<\/p>/gi, "<p><br></p>")
    .replace(/(?:<p><br><\/p>\s*){3,}/gi, "<p><br></p><p><br></p>")
    .replace(/^(?:<p><br><\/p>\s*)+/i, "")
    .replace(/(?:\s*<p><br><\/p>)+$/i, "")
    .trim();

  return value === "<p><br></p>" ? "" : value;
};

export const htmlToPlainText = (text) => {
  const source = String(text || "");
  if (!isHtmlContent(source)) return source;

  if (typeof document === "undefined") {
    return source
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&#39;/gi, "'")
      .replace(/&quot;/gi, '"');
  }

  const div = document.createElement("div");
  div.innerHTML = source;
  const content = div.innerText || div.textContent || "";
  return content.replace(/\u00A0/g, " ");
};

export const plainTextToEditorHtml = (text) => {
  const source = String(text || "");
  if (!source.trim()) return "";
  if (isHtmlContent(source)) return normalizeQuillHtml(source);

  const paragraphs = source
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((paragraph) => {
      const lines = paragraph
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      if (!lines.length) return "";

      // Refluye saltos de línea automáticos de OCR/parseo para evitar texto "cortado".
      const looksLikeList = lines.every((line) => /^([0-9]+[.)]|[-*•])\s+/.test(line));
      const merged = looksLikeList ? lines.join("<br />") : lines.join(" ");
      return escapeHtml(merged).replace(/&lt;br \/&gt;/g, "<br />");
    })
    .map((paragraph) => paragraph.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>"));

  return paragraphs.map((paragraph) => `<p>${paragraph}</p>`).join("");
};

export const hasRenderableText = (text) => normalizeDisplayLine(toPlainContractText(text || "")).length > 0;
export const normalizeContractTitleText = (titleValue) =>
  toPlainContractText(titleValue || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => normalizeDisplayLine(line))
    .filter(Boolean)
    .join("\n")
    .trim();

export const toPlainContractText = (text) => htmlToPlainText(text);

export const stripBoldMarkers = (text) => toPlainContractText(text).replace(/\*\*([^*]+)\*\*/g, "$1");

export const normalizeDisplayLine = (text) =>
  stripBoldMarkers(text)
    .replace(/\s+/g, " ")
    .trim();

export const splitParagraphs = (text) =>
  toPlainContractText(text)
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

export const splitParagraphsRaw = (text) =>
  String(text || "")
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

export const htmlToTextPreserveBoldMarkers = (html) =>
  String(html || "")
    .replace(/<\s*(strong|b)\b[^>]*>/gi, "**")
    .replace(/<\s*\/\s*(strong|b)\s*>/gi, "**")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<li\b[^>]*>/gi, "- ")
    .replace(/<\/(p|div|li|h[1-6]|section)>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\\\*/g, "*")
    .replace(/\*{3,}/g, "**")
    .replace(/\*\*\s+\*\*/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export const isIntroClause = (clause) => /introducci[oó]n/i.test(String(clause?.title || ""));

export const isSignatureHeading = (line) =>
  /^(firmas?|suscriben|conformidad|el\s+empleador|el\s+trabajador)$/i.test(
    normalizeDisplayLine(line)
  );

export const isSignatureParagraph = (text) => {
  const line = normalizeDisplayLine(text);
  if (!line) return false;
  if (isSignatureHeading(line)) return true;
  if (/^_{5,}$/.test(line)) return true;
  if (/^dni[:\s]/i.test(line)) return true;
  if (/el\s+empleador/i.test(line) && /el\s+trabajador/i.test(line)) return true;
  return false;
};

export const isSignatureClause = (clause) => {
  const title = normalizeDisplayLine(clause?.title || "");
  const content = normalizeDisplayLine(clause?.content || "");
  if (/firma|suscripci[oó]n|conformidad/i.test(title)) return true;
  if (/el empleador|el trabajador/.test(content) && /_{5,}/.test(clause?.content || "")) return true;
  return false;
};

export const extractOriginalContractTitle = (templateName, clausulas) => {
  const intro = clausulas.find(isIntroClause);
  if (!intro) return String(templateName || "CONTRATO DE TRABAJO").trim();

  const lines = toPlainContractText(intro.content || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const firstTitleIdx = lines.findIndex((line) => /contrato\s+de\s+trabajo/i.test(line));
  if (firstTitleIdx < 0) return String(templateName || "CONTRATO DE TRABAJO").trim();

  const collected = [];
  for (let i = firstTitleIdx; i < lines.length; i += 1) {
    const current = lines[i];
    if (/^el\s+empleador[:]?/i.test(current) || /^el\s+trabajador[:]?/i.test(current)) break;
    if (/^cl[áa]usula/i.test(current)) break;
    collected.push(current);
    if (collected.length >= 3) break;
  }

  if (!collected.length) return String(templateName || "CONTRATO DE TRABAJO").trim();
  return collected.join("\n");
};

export const removeTitleFromIntroduction = (introText, title) => {
  let introLines = String(introText || "").split("\n");
  const titleLines = String(title || "")
    .split("\n")
    .map((l) => normalizeDisplayLine(l))
    .filter(Boolean);

  if (!titleLines.length) return introText;

  const titleSet = new Set(titleLines);
  const fullTitleNormalized = titleLines.join(" ").replace(/\s+/g, " ").trim();

  // Remueve repetición del título al inicio del bloque de introducción.
  while (introLines.length) {
    const candidate = normalizeDisplayLine(introLines[0]);
    if (!candidate) {
      introLines.shift();
      continue;
    }
    if (titleSet.has(candidate) || /contrato\s+de\s+trabajo/i.test(candidate)) {
      introLines.shift();
      continue;
    }
    break;
  }

  // Si aun quedó el título completo en una línea, lo elimina.
  introLines = introLines.filter((line, idx) => {
    if (idx > 3) return true;
    const normalized = normalizeDisplayLine(line);
    if (!normalized) return true;
    return normalized !== fullTitleNormalized;
  });

  return introLines.join("\n").trim();
};

export const isTitleDuplicateParagraph = (paragraph, title) => {
  const p = normalizeDisplayLine(paragraph).toLowerCase();
  const t = normalizeDisplayLine(title).toLowerCase();
  if (!p || !t) return false;
  if (p === t) return true;
  if (p.length <= t.length + 12 && p.includes(t)) return true;
  return false;
};

export const stripLeadingTitleFromParagraph = (paragraph, title) => {
  const titleLines = String(title || "")
    .split("\n")
    .map((l) => normalizeDisplayLine(l))
    .filter(Boolean);
  if (!titleLines.length) return paragraph;

  const lines = String(paragraph || "").split("\n");
  let cursor = 0;

  while (cursor < lines.length) {
    const current = normalizeDisplayLine(lines[cursor]);
    if (!current) {
      cursor += 1;
      continue;
    }

    if (
      titleLines.includes(current) ||
      (current.length <= titleLines[0].length + 12 &&
        /contrato\s+de\s+trabajo/i.test(current))
    ) {
      cursor += 1;
      continue;
    }
    break;
  }

  return lines.slice(cursor).join("\n").trim();
};

export const renderTextWithPlaceholders = (text, data, options = {}) => {
  const { boldVariables = false } = options;
  const source = String(text || "");
  const htmlMode = isHtmlContent(source);
  const safeData = data && typeof data === "object" ? data : {};

  return source.replace(/(S\/\.?\s*)?{{\s*([a-zA-Z0-9_]+)\s*}}/gi, (_, prefix, token) => {
    const canonicalKey = canonicalizeContractVariableKey(token);
    let value = String(safeData[token] || safeData[canonicalKey] || "").trim();

    // Fallbacks inteligentes para pares de campos relacionados
    if (!value) {
      if (canonicalKey === "representante_legal") {
        value = String(safeData.firma_empleador_nombre || safeData.representante_legal_nombre || "").trim();
      } else if (canonicalKey === "firma_empleador_nombre") {
        value = String(safeData.representante_legal || safeData.representante_legal_nombre || "").trim();
      } else if (canonicalKey === "representante_legal_cargo") {
        value = String(safeData.firma_empleador_cargo || safeData.empleador_representante_legal_cargo || "").trim();
      } else if (canonicalKey === "firma_empleador_cargo") {
        value = String(safeData.representante_legal_cargo || safeData.empleador_representante_legal_cargo || "").trim();
      } else if (canonicalKey === "trabajador_nombre") {
        value = String(safeData.firma_trabajador_nombre || safeData.nombre_trabajador || "").trim();
      } else if (canonicalKey === "firma_trabajador_nombre") {
        value = String(safeData.trabajador_nombre || safeData.nombre_trabajador || "").trim();
      } else if (canonicalKey === "trabajador_dni") {
        value = String(safeData.firma_trabajador_dni || safeData.dni || "").trim();
      } else if (canonicalKey === "firma_trabajador_dni") {
        value = String(safeData.trabajador_dni || safeData.dni || "").trim();
      } else if (canonicalKey === "trabajador_telefono") {
        value = String(safeData.telefono || safeData.celular || safeData.phone || safeData.employee_phone || "").trim();
      } else if (canonicalKey === "trabajador_correo") {
        value = String(safeData.correo || safeData.email || safeData.employee_email || "").trim();
      } else if (canonicalKey === "periodo_prueba_meses") {
        value = "3";
      } else if (canonicalKey === "fecha_fin_periodo_prueba") {
        const fInicio = safeData.fecha_inicio || safeData.fechaInicio || safeData.fecha_inicio_contrato;
        if (fInicio) {
          const finPruebaIso = calcularFinPeriodoPrueba(fInicio, safeData.periodo_prueba_meses || 3);
          value = formatDateEsPe(finPruebaIso);
        }
      }
    }

    // Formateo especial para sueldo
    if (canonicalKey === "sueldo" && value) {
      value = formatSoles(value);
      // Si el texto del contrato ya traía "S/ " antes de {{sueldo}} y nuestro valor ya trae "S/ ",
      // consumimos el prefix para no generar "S/ S/ 1,800.00"
      if (prefix && /^s\/\.?\s*/i.test(value)) {
        if (htmlMode) {
          const escaped = escapeHtml(value);
          return boldVariables ? `<strong>${escaped}</strong>` : escaped;
        }
        return boldVariables ? `**${value}**` : value;
      }
    } else if (canonicalKey === "monto_sueldo" && value) {
      value = formatMonto(value);
    }

    if (!value) return "";

    const fullValue = (prefix || "") + value;
    if (htmlMode) {
      const escaped = escapeHtml(fullValue);
      return boldVariables ? `<strong>${escaped}</strong>` : escaped;
    }
    return boldVariables ? `**${fullValue}**` : fullValue;
  });
};

export const buildContratoSections = (clausulas, data, templateName = "CONTRATO DE TRABAJO", options = {}) => {
  const { boldVariables = false, contractTitle = "" } = options;

  const intro = clausulas.find(isIntroClause);
  const regularClauses = clausulas.filter((c) => !isIntroClause(c) && !isSignatureClause(c));

  const detectedTitle = extractOriginalContractTitle(templateName, clausulas);
  const manualTitle = normalizeContractTitleText(contractTitle);
  const title = manualTitle || detectedTitle;
  const manualTitleHtml = normalizeQuillHtml(
    sanitizeEditorHtml(isHtmlContent(contractTitle) ? contractTitle : plainTextToEditorHtml(manualTitle))
  );
  const titleHtml = hasRenderableText(manualTitleHtml)
    ? manualTitleHtml
    : plainTextToEditorHtml(title);
  const introSource = intro ? String(intro.content || "") : "";
  const introPlainSource = intro ? toPlainContractText(introSource) : "";
  const introProcessed = intro
    ? renderTextWithPlaceholders(removeTitleFromIntroduction(introPlainSource, title), data, { boldVariables })
    : "";
  const introHtmlCandidate = intro && isHtmlContent(introSource)
    ? removeLeadingTitleFromHtml(renderTextWithPlaceholders(introSource, data, { boldVariables }), title)
    : "";
  const introHtml = hasRenderableText(introHtmlCandidate) ? introHtmlCandidate : "";

  const introParagraphs = intro
    ? splitParagraphs(introProcessed)
        .filter((p) => !isSignatureParagraph(p))
        .map((p) => stripLeadingTitleFromParagraph(p, title))
        .filter((p) => p && !isTitleDuplicateParagraph(p, title))
    : [];

  const clauseBlocks = regularClauses
    .map((c) => {
      const source = String(c.content || "");
      const plainSource = toPlainContractText(source);
      const renderedPlain = renderTextWithPlaceholders(plainSource, data, { boldVariables });
      const renderedHtml = isHtmlContent(source) ? renderTextWithPlaceholders(source, data, { boldVariables }) : "";

      const normalizedHtml = normalizeQuillHtml(sanitizeEditorHtml(renderedHtml));
      const plainParagraphs = splitParagraphs(renderedPlain).filter((p) => !isSignatureParagraph(p));
      const htmlParagraphs = splitParagraphs(toPlainContractText(normalizedHtml)).filter((p) => !isSignatureParagraph(p));

      const plainTextFootprint = normalizeDisplayLine(plainParagraphs.join(" "));
      const htmlTextFootprint = normalizeDisplayLine(toPlainContractText(normalizedHtml));
      const htmlCoverageLooksOk =
        !plainTextFootprint ||
        htmlTextFootprint.length >= Math.max(20, Math.floor(plainTextFootprint.length * 0.7));

      const safeHtml =
        hasRenderableText(normalizedHtml) && htmlCoverageLooksOk
          ? normalizedHtml
          : "";

      const fallbackOneLiner = normalizeDisplayLine(renderedPlain);
      const finalParagraphs = plainParagraphs.length
        ? plainParagraphs
        : htmlParagraphs.length
          ? htmlParagraphs
          : fallbackOneLiner
            ? [fallbackOneLiner]
            : [];
      return {
        paragraphs: finalParagraphs,
        html: safeHtml,
      };
    })
    .filter((c) => c.paragraphs.length || c.html);

  const signatures = {
    employerName:
      datosOrEmpty(data, [
        "firma_empleador_nombre",
        "employer_legal_representative_name_signature",
        "representante_legal",
        "empleador_representante_legal_nombre",
      ]),
    employerRole:
      datosOrEmpty(data, [
        "firma_empleador_cargo",
        "employer_legal_representative_title",
        "representante_legal_cargo",
        "empleador_representante_legal_cargo",
      ]),
    employeeName:
      datosOrEmpty(data, [
        "firma_trabajador_nombre",
        "employee_name_signature",
        "trabajador_nombre",
        "trabajador_nombre_completo",
      ]),
    employeeDni:
      datosOrEmpty(data, [
        "firma_trabajador_dni",
        "employee_dni_signature",
        "trabajador_dni",
        "employee_dni",
      ]),
  };

  return { title, titleHtml, introParagraphs, introHtml, clauses: clauseBlocks, signatures };
};

export const datosOrEmpty = (data, keys = []) => {
  for (const key of keys) {
    const value = String(data?.[key] || "").trim();
    if (value) return value;
  }
  return "";
};

export const renderInlineBold = (text) => {
  const parts = String(text || "").split(/(\*\*[\s\S]+?\*\*)/g);
  return parts.map((part, index) => {
    const isBold = /^\*\*[\s\S]+?\*\*$/.test(part);
    if (!isBold) return <span key={`txt-${index}`}>{part}</span>;
    return (
      <strong key={`b-${index}`} style={{ fontWeight: 700 }}>
        {part.slice(2, -2)}
      </strong>
    );
  });
};

export const renderRichParagraphs = (text) => {
  if (isHtmlContent(text)) {
    const html = normalizeQuillHtml(text);
    if (!html) return null;
    return (
      <div
        style={{
          textAlign: "justify",
          lineHeight: 1.6,
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  const blocks = String(text || "")
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);

  return blocks.map((block, index) => {
    const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
    return (
      <p
        key={`p-${index}`}
        style={{
          marginBottom: "0.8rem",
          textAlign: "justify",
          lineHeight: 1.6,
        }}
      >
        {lines.map((line, lineIndex) => (
          <React.Fragment key={`l-${index}-${lineIndex}`}>
            {renderInlineBold(line)}
            {lineIndex < lines.length - 1 ? <br /> : null}
          </React.Fragment>
        ))}
      </p>
    );
  });
};

export const sanitizeEditorHtml = (html) =>
  String(html || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .trim();

export const plainTextWithBoldToHtml = (text) =>
  escapeHtml(String(text || ""))
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br />");

export const paragraphListToHtml = (paragraphs = []) =>
  paragraphs.map((paragraph) => `<p>${plainTextWithBoldToHtml(paragraph)}</p>`).join("");

export const removeLeadingTitleFromHtml = (html, title) => {
  const source = normalizeQuillHtml(sanitizeEditorHtml(html));
  if (!source || !isHtmlContent(source) || typeof document === "undefined") return source;

  const titleLines = String(title || "")
    .split("\n")
    .map((line) => normalizeDisplayLine(line))
    .filter(Boolean);
  if (!titleLines.length) return source;

  const fullTitle = titleLines.join(" ").replace(/\s+/g, " ").trim();
  const wrapper = document.createElement("div");
  wrapper.innerHTML = source;

  const blocks = Array.from(wrapper.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6")).filter(
    (node) => !node.closest("li")
  );

  for (const block of blocks) {
    const line = normalizeDisplayLine(block.textContent || "");
    if (!line) {
      block.remove();
      continue;
    }

    const looksLikeTitle =
      titleLines.includes(line) ||
      line === fullTitle ||
      (/contrato\s+de\s+trabajo/i.test(line) && line.length <= fullTitle.length + 18);

    if (!looksLikeTitle) break;
    block.remove();
  }

  return normalizeQuillHtml(wrapper.innerHTML);
};

export const normalizeParsedClauseContent = (title, content) => {
  const source = String(content || "");
  if (!source.trim()) return "";
  if (isHtmlContent(source)) return normalizeQuillHtml(source);

  const clauseMeta = { title: String(title || ""), content: source };
  const preserveLineBreaks = isSignatureClause(clauseMeta);

  const normalized = source
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((paragraph) => {
      const lines = paragraph
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      if (!lines.length) return "";
      if (preserveLineBreaks) return lines.join("\n");
      return lines.join(" ");
    })
    .filter(Boolean)
    .join("\n\n");

  return normalized.trim();
};

export const buildContratoPdfHtml = (sections, options = {}) => {
  const { innerPaddingPx = 0 } = options;
  const safeTitleLines = String(sections?.title || "")
    .split("\n")
    .map((line) => normalizeDisplayLine(line))
    .filter(Boolean)
    .map((line) => `<div>${plainTextWithBoldToHtml(line)}</div>`)
    .join("");
  const titleHtmlRaw = normalizeQuillHtml(sanitizeEditorHtml(sections?.titleHtml || ""));
  const titleHtml = hasRenderableText(titleHtmlRaw) ? titleHtmlRaw : safeTitleLines;

  const introHtmlRaw = normalizeQuillHtml(sanitizeEditorHtml(sections?.introHtml || ""));
  const introHtml = hasRenderableText(introHtmlRaw) ? introHtmlRaw : paragraphListToHtml(sections?.introParagraphs || []);

  const clausesHtml = (sections?.clauses || [])
    .map((clause) => {
      const bodyHtmlRaw = normalizeQuillHtml(sanitizeEditorHtml(clause?.html || ""));
      const bodyHtml = hasRenderableText(bodyHtmlRaw) ? bodyHtmlRaw : paragraphListToHtml(clause?.paragraphs || []);
      return `<section class="contract-clause">${bodyHtml}</section>`;
    })
    .join("");

  const employerName = escapeHtml(sections?.signatures?.employerName || "");
  const employerRole = escapeHtml(sections?.signatures?.employerRole || "");
  const employeeName = escapeHtml(sections?.signatures?.employeeName || "");
  const employeeDni = escapeHtml(sections?.signatures?.employeeDni || "");

  const signaturesHtml =
    employerName || employeeName
      ? `
      <section class="contract-signatures">
        <div class="contract-signature-box">
          <div class="contract-signature-line"></div>
          <div class="contract-signature-label">EL EMPLEADOR</div>
          ${employerName ? `<div class="contract-signature-value"><strong>${employerName}</strong></div>` : ""}
          ${employerRole ? `<div class="contract-signature-subvalue"><strong>${employerRole}</strong></div>` : ""}
        </div>
        <div class="contract-signature-box">
          <div class="contract-signature-line"></div>
          <div class="contract-signature-label">EL TRABAJADOR</div>
          ${employeeName ? `<div class="contract-signature-value"><strong>${employeeName}</strong></div>` : ""}
          ${employeeDni ? `<div class="contract-signature-subvalue"><strong>DNI: ${employeeDni}</strong></div>` : ""}
        </div>
      </section>
    `
      : "";

  return `
      <div class="contract-pdf-page">
      <div class="contract-pdf-root">
      <style>
        .contract-pdf-page {
          width: 100%;
          box-sizing: border-box;
        }
        .contract-pdf-root {
          font-family: "Times New Roman", serif;
          color: #111;
          font-size: 14px;
          line-height: 1.6;
          background: #fff;
          box-sizing: border-box;
          width: 100%;
          max-width: 100%;
          padding: 0 ${innerPaddingPx}px;
          margin: 0 !important;
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .contract-pdf-root,
        .contract-pdf-root * {
          box-sizing: border-box;
          max-width: 100%;
        }
        .contract-pdf-root p,
        .contract-pdf-root li,
        .contract-pdf-root div,
        .contract-pdf-root span,
        .contract-pdf-root strong {
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .contract-pdf-title {
          text-align: center;
          font-weight: 700;
          margin-bottom: 18px;
          white-space: pre-line;
          font-size: 16px;
        }
        .contract-pdf-root p {
          margin: 0 0 10px;
          text-align: justify;
        }
        .contract-pdf-root ul,
        .contract-pdf-root ol {
          margin: 0 0 12px 24px;
          padding: 0;
        }
        .contract-pdf-root li {
          margin-bottom: 4px;
          text-align: justify;
        }
        .contract-pdf-root .ql-align-center {
          text-align: center;
        }
        .contract-pdf-root .ql-align-right {
          text-align: right;
        }
        .contract-pdf-root .ql-align-justify {
          text-align: justify;
        }
        .contract-clause {
          margin-bottom: 8px;
        }
        .contract-signatures {
          margin-top: 76px;
          display: flex;
          gap: 42px;
        }
        .contract-signature-box {
          flex: 1;
          text-align: center;
          min-height: 120px;
          padding-top: 24px;
        }
        .contract-signature-line {
          border-top: 1px solid #444;
          margin: 0 10px 8px;
        }
        .contract-signature-label {
          font-weight: 700;
          letter-spacing: 0.3px;
          margin-bottom: 6px;
        }
        .contract-signature-value {
          margin-top: 4px;
        }
        .contract-signature-subvalue {
          margin-top: 2px;
          font-size: 13px;
        }
      </style>
      <div class="contract-pdf-title">${titleHtml}</div>
      ${introHtml}
      ${clausesHtml}
      ${signaturesHtml}
    </div>
    </div>
  `;
};

export const isBoldFont = (fontName) => /bold|black|demi|heavy|semibold/i.test(String(fontName || ""));

export const normalizeInlineSpacing = (text) =>
  String(text || "")
    .replace(/\s+([,.;:!?])/g, "$1")
    .replace(/([([])\s+/g, "$1")
    .replace(/\s+([)\]])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

export const collapseAdjacentBoldTokens = (line) => {
  let next = String(line || "");
  let previous = "";
  while (previous !== next) {
    previous = next;
    next = next.replace(/\*\*([^*]+)\*\*\s+\*\*([^*]+)\*\*/g, "**$1 $2**");
  }
  return next;
};

export const extractPageTextPreservingFormat = (textContent) => {
  const items = Array.isArray(textContent?.items) ? textContent.items : [];
  if (!items.length) return "";

  const lines = [];
  let currentLine = [];
  let previousY = null;

  const flushLine = (lineGap = 0) => {
    if (!currentLine.length) {
      if (lineGap > 14) lines.push("");
      return;
    }

    const normalized = collapseAdjacentBoldTokens(normalizeInlineSpacing(currentLine.join(" ")));
    if (normalized) lines.push(normalized);
    if (lineGap > 14) lines.push("");
    currentLine = [];
  };

  items.forEach((item) => {
    const raw = String(item?.str || "");
    const token = raw.trim();
    if (!token) return;

    const y = Number(item?.transform?.[5] || 0);
    const lineGap = previousY === null ? 0 : Math.abs(previousY - y);
    const changedLine = previousY !== null && lineGap > 2.2;
    if (changedLine) flushLine(lineGap);

    const decoratedToken = isBoldFont(item?.fontName) ? `**${token}**` : token;
    currentLine.push(decoratedToken);

    if (item?.hasEOL) flushLine(0);
    previousY = y;
  });

  flushLine(0);

  return lines
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

